import { Component, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Messaging, getToken, isSupported } from '@angular/fire/messaging';
import { MessageService } from 'primeng/api';
import { AuthenticationService, AuthFlowResult } from '../services/authentication.service';
import { FcmTokenService } from '../services/fcm-token.service';

import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    ToastModule,
    ProgressBarModule
  ],
  providers: [MessageService],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  activeTab: 'tamer' | 'supplier' = 'tamer';
  email = '';
  password = '';
  otpCode = '';
  isLoading = false;
  showSupplierEmailForm = false;
  showOtpStep = false;
  otpMaskedEmail = '';
  otpExpiresAt = '';
  otpResendAvailableAt = '';

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private fcmTokenService: FcmTokenService,
    private messaging: Messaging,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.authService.getAuthState().subscribe((user) => {
      if (user) {
        if (user.firebaseId) {
          this.initializeFCM(user.firebaseId);
        }

        const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/ai-studio';
        this.router.navigateByUrl(returnUrl);
      }
    });
  }

  switchTab(tab: 'tamer' | 'supplier'): void {
    this.activeTab = tab;
    this.resetTransientState();
  }

  openSupplierEmailForm(): void {
    this.activeTab = 'supplier';
    this.showSupplierEmailForm = true;
    this.showOtpStep = false;
  }

  onSamlLogin(): void {
    this.isLoading = true;
    this.authService.signInWithSaml().subscribe({
      next: (result) => this.handleAuthFlowResult(result),
      error: (error) => this.handleError('Tamer SSO failed.', error)
    });
  }

  onGoogleLogin(): void {
    this.activeTab = 'supplier';
    this.isLoading = true;
    this.authService.signInWithGoogle().subscribe({
      next: (result) => this.handleAuthFlowResult(result),
      error: (error) => this.handleError('Google sign-in failed.', error)
    });
  }

  onMicrosoftLogin(): void {
    this.activeTab = 'supplier';
    this.isLoading = true;
    this.authService.signInWithMicrosoft().subscribe({
      next: (result) => this.handleAuthFlowResult(result),
      error: (error) => this.handleError('Microsoft sign-in failed.', error)
    });
  }

  onEmailSignIn(form?: NgForm): void {
    if (!this.ensureValidForm(form)) {
      return;
    }

    this.isLoading = true;
    this.authService.signInWithEmailPassword(this.email, this.password).subscribe({
      next: (result) => this.handleAuthFlowResult(result),
      error: (error) => this.handleError('Supplier sign-in failed.', error)
    });
  }

  onCreateSupplierPassword(form?: NgForm): void {
    if (!this.ensureValidForm(form)) {
      return;
    }

    this.isLoading = true;
    this.authService.signUpWithEmailPassword(this.email, this.password).subscribe({
      next: (result) => this.handleAuthFlowResult(result),
      error: (error) => this.handleError('Could not create your supplier password account.', error)
    });
  }

  onVerifyOtp(): void {
    if (!this.otpCode.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'OTP Required',
        detail: 'Enter the OTP sent to your email address.'
      });
      return;
    }

    this.isLoading = true;
    this.authService.verifySupplierOtp(this.otpCode.trim()).subscribe({
      next: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'OTP Verified',
          detail: 'Supplier verification is complete. Redirecting you now.'
        });
      },
      error: (error) => this.handleError('OTP verification failed.', error)
    });
  }

  onResendOtp(): void {
    this.isLoading = true;
    this.authService.resendSupplierOtp().subscribe({
      next: (result) => this.handleAuthFlowResult(result),
      error: (error) => this.handleError('Could not resend the OTP.', error)
    });
  }

  onCancelOtp(): void {
    this.isLoading = true;
    this.authService.signOut().subscribe({
      next: () => {
        this.isLoading = false;
        this.resetTransientState();
      },
      error: (error) => this.handleError('Could not cancel the supplier login flow.', error)
    });
  }

  onForgotPassword(): void {
    if (!this.email) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Email Required',
        detail: 'Enter your supplier email address first, then request a password reset.'
      });
      return;
    }

    this.isLoading = true;
    this.authService.sendPasswordResetEmail(this.email).subscribe({
      next: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Check Your Email',
          detail: 'A password reset link has been sent to your email address.'
        });
      },
      error: (error) => this.handleError('Could not send the password reset email.', error)
    });
  }

  canResendOtp(): boolean {
    if (!this.otpResendAvailableAt) {
      return true;
    }

    return new Date(this.otpResendAvailableAt).getTime() <= Date.now();
  }

  private handleAuthFlowResult(result: AuthFlowResult): void {
    this.isLoading = false;

    if (result.stage === 'authenticated') {
      this.messageService.add({
        severity: 'success',
        summary: 'Signed In',
        detail: 'Authentication completed successfully.'
      });
      return;
    }

    if (result.stage === 'otp_required') {
      this.showOtpStep = true;
      this.showSupplierEmailForm = false;
      this.activeTab = 'supplier';
      this.otpCode = '';
      this.otpMaskedEmail = result.maskedEmail ?? '';
      this.otpExpiresAt = result.expiresAt ?? '';
      this.otpResendAvailableAt = result.resendAvailableAt ?? '';
      this.messageService.add({
        severity: 'info',
        summary: 'OTP Required',
        detail: result.message ?? 'Enter the OTP sent to your email to continue.'
      });
      return;
    }

    this.showOtpStep = false;
    this.showSupplierEmailForm = true;
    this.activeTab = 'supplier';
    this.messageService.add({
      severity: 'success',
      summary: 'Verify Your Email',
      detail: result.message ?? 'Please verify your email address before signing in.'
    });
  }

  private handleError(summary: string, error: unknown): void {
    this.isLoading = false;
    console.error(summary, error);
    this.messageService.add({
      severity: 'error',
      summary,
      detail: this.extractErrorMessage(error)
    });
  }

  private extractErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      const candidate = error as {
        error?: { message?: string };
        message?: string;
        code?: string;
      };

      if (candidate.error?.message) {
        return candidate.error.message;
      }

      if (candidate.message) {
        return candidate.message;
      }

      if (candidate.code) {
        return candidate.code;
      }
    }

    return 'Something went wrong. Please try again.';
  }

  private ensureValidForm(form?: NgForm): boolean {
    if (!form || !form.invalid) {
      return true;
    }

    Object.keys(form.controls).forEach((field) => {
      form.controls[field].markAsTouched({ onlySelf: true });
    });

    return false;
  }

  private resetTransientState(): void {
    this.showOtpStep = false;
    this.otpCode = '';
    this.otpMaskedEmail = '';
    this.otpExpiresAt = '';
    this.otpResendAvailableAt = '';
    this.showSupplierEmailForm = false;
  }

  private async initializeFCM(userId: string): Promise<void> {
    try {
      const supported = await isSupported();
      if (!supported) {
        return;
      }

      Notification.requestPermission().then((permission) => {
        if (permission !== 'granted') {
          return;
        }

        getToken(this.messaging, { vapidKey: `${environment.fcmWebPushKeyPair}` }).then((currentToken) => {
          if (!currentToken) {
            return;
          }

          this.fcmTokenService.updateFcmToken(userId, currentToken).subscribe({
            next: () => console.log('FCM token updated on server successfully.'),
            error: (err) => console.error('Error updating FCM token on server:', err)
          });
        }).catch((err) => {
          console.error('An error occurred while retrieving token.', err);
        });
      });
    } catch (err) {
      console.error('Error initializing FCM:', err);
    }
  }
}
